import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, ArrowUpRight } from "lucide-react";
import { formatPlanDate, usePlanFeatures } from "@/hooks/usePlanFeatures";
import axiosInstance, { getAuthToken } from "@/utils/appsettings";
import { onCreditUpdate } from "@/utils/creditEvents";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { getWorkspaceQueryString } from "@/utils/workspaceUtils";
import { calculateCreditSums } from "@/utils/billingUtils";
import { fetchBillingSummary } from "@/lib/billingSummary";

const formatCredits = (n: number) =>
  n.toLocaleString(undefined, { maximumFractionDigits: n < 100 ? 2 : 0 });

// Read-only mirror of the profile menu's balance; it listens for credit events but never emits, so it can't start a refetch loop
export const SidebarCredits = ({ linkState }: { linkState?: unknown }) => {
  const { activeWorkspace, isLoading: workspaceLoading } = useWorkspace();
  const { isExpired, planName: expiredPlanName, currentPeriodEnd } = usePlanFeatures();
  const expiredOn = formatPlanDate(currentPeriodEnd);
  const [planName, setPlanName] = useState("");
  const [allocations, setAllocations] = useState<any[]>([]);
  const [usageSummary, setUsageSummary] = useState<any>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (workspaceLoading || !activeWorkspace?.id) return;
    let cancelled = false;
    const fetchCredits = async () => {
      try {
        const data = await fetchBillingSummary(getWorkspaceQueryString(activeWorkspace), { token: getAuthToken() });
        if (cancelled) return;
        setPlanName(data?.subscription?.plan || "Free");
        setAllocations(data?.allocations || []);
        setUsageSummary(data?.usage_summary || {});
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(false);
      }
    };
    fetchCredits();
    const cleanup = onCreditUpdate(() => fetchCredits());
    // Catches plan/credit changes made elsewhere (another device, a Stripe tab) that no event reaches
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchCredits();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      cleanup();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [activeWorkspace, workspaceLoading]);

  const sums = useMemo(
    () => calculateCreditSums(allocations, planName, usageSummary),
    [allocations, planName, usageSummary],
  );

  const remaining = sums.totalRemaining || 0;
  const total = sums.totalAllocated || 0;
  const pct = total > 0 ? Math.min(100, (100 * remaining) / total) : 0;

  if (isExpired) {
    return (
      <div className="rounded-[10px] border border-danger/25 bg-danger-bg px-3 py-2.5 mb-2" data-testid="sidebar-plan-expired">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-danger">
          <AlertCircle className="w-3.5 h-3.5" /> {expiredPlanName} plan expired
        </div>
        {expiredOn && <p className="mt-1 text-[11px] text-muted-foreground">Ended {expiredOn}</p>}
        <Link
          to="/billing/manage"
          state={linkState}
          className="mt-2 flex items-center justify-between text-[11px] font-medium text-plum hover:underline"
        >
          Renew your plan <ArrowUpRight className="w-3 h-3" />
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-line bg-canvas px-3 py-2.5 mb-2">
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{planName ? `${planName} credits` : "Credits"}</span>
        <strong className="font-semibold text-foreground" data-testid="sidebar-credits-remaining">
          {loaded ? formatCredits(remaining) : "—"}
          {loaded && total > 0 && (
            <span className="font-normal text-muted-foreground"> / {formatCredits(total)}</span>
          )}
        </strong>
      </div>
      <div className="h-1 rounded-[3px] bg-input overflow-hidden my-2.5">
        <span className="block h-full rounded-[inherit] bg-gradient-brand transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <Link
        to="/billing/manage"
        state={linkState}
        className="flex items-center justify-between text-[11px] font-medium text-plum hover:underline"
      >
        Manage your plan <ArrowUpRight className="w-3 h-3" />
      </Link>
    </div>
  );
};
